import { useState, useEffect } from "react";

interface NavbarProps {
  onCTAClick: () => void;
}

export default function Navbar({ onCTAClick }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const navLinks = [
    { label: "How It Works", href: "#how-it-works" },
    { label: "Pricing", href: "#pricing" },
    { label: "Demo", href: "#demo" },
    { label: "Territory", href: "#territory" },
    { label: "About", href: "#about" },
    { label: "FAQ", href: "#faq" },
  ];

  const closeMenu = () => setMobileOpen(false);

  return (
    <>
      <nav
        className={`fixed left-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? "top-0 bg-white border-b border-gray-100"
            : "top-14 sm:top-9 bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-16">
          {/* Logo */}
          <a href="/" className="text-navy font-extrabold text-xl tracking-tight whitespace-nowrap">
            IMPERIUM
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="text-[#4a5568] text-sm font-medium hover:text-navy transition-colors cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA + mobile hamburger */}
          <div className="flex items-center gap-3">
            <button
              onClick={onCTAClick}
              className="hidden md:inline-flex items-center gap-2 bg-primary text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Check Market Availability
            </button>

            {/* Hamburger — larger touch target */}
            <button
              className="md:hidden w-11 h-11 flex items-center justify-center text-navy rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={() => setMobileOpen((o) => !o)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
            >
              <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
            </button>
          </div>
        </div>

        {/* Mobile dropdown panel */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-5 pt-1 pb-6 flex flex-col">
            {/* Nav links — full-width with big touch targets */}
            {navLinks.map((link, i) => (
              <a
                key={link.label}
                href={link.href}
                onClick={closeMenu}
                className={`flex items-center justify-between text-[#0b1f33] text-[15px] font-medium py-4 cursor-pointer hover:text-primary transition-colors ${
                  i < navLinks.length - 1 ? "border-b border-gray-100" : ""
                }`}
              >
                {link.label}
                <span className="w-5 h-5 flex items-center justify-center text-gray-300">
                  <i className="ri-arrow-right-s-line text-base" />
                </span>
              </a>
            ))}

            {/* CTA button */}
            <div className="pt-5">
              <button
                onClick={() => { onCTAClick(); closeMenu(); }}
                className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white text-sm font-bold px-5 py-4 rounded-xl cursor-pointer whitespace-nowrap hover:bg-orange-600 transition-colors"
              >
                Check If Your Market Is Still Available
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </span>
              </button>
              <p className="text-center text-[#4a5568] text-xs font-medium mt-2.5">
                Only one title partner per market.
              </p>
            </div>
          </div>
        )}
      </nav>

      {/* Backdrop — tapping outside closes the menu */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/25 z-40 md:hidden"
          onClick={closeMenu}
          aria-hidden="true"
        />
      )}
    </>
  );
}
